// src/components/LandlordSidebar.jsx
import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  Filter,
  Search,
  Building2,
  CarFront,
  ChevronRight,
  CheckSquare,
  Square,
} from "lucide-react";
import { getAny } from "../utils/storage";

export default function LandlordSidebar({
  open,
  setOpen,
  mobileOpen,
  setMobileOpen,
  user,
}) {
  const [selected, setSelected] = useState(() => {
    try {
      const raw = getAny("selected_facilities");
      return raw ? JSON.parse(raw).map(String) : [];
    } catch {
      return [];
    }
  });
  const [query, setQuery] = useState("");

  // Pull user (props or localStorage)
  const storedUser = getAny("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : null;

  const facilities = useMemo(
    () =>
      (user?.facilities || parsedUser?.facilities || []).map((f) => ({
        id: String(f.facility_id),
        name: f.facility_name,
      })),
    [user, parsedUser]
  );

  const facilityCount =
    user?.total_facilities ?? parsedUser?.total_facilities ?? facilities.length;

  // Search filter
  const filteredFacilities = facilities.filter(
    (f) =>
      f.name.toLowerCase().includes(query.toLowerCase()) ||
      String(f.id).includes(query)
  );

  // Selection helpers
  const allSelected =
    facilities.length > 0 && selected.length === facilities.length;
  const allFilteredSelected =
    filteredFacilities.length > 0 &&
    filteredFacilities.every((f) => selected.includes(String(f.id)));

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const strId = String(id);
      const nextSelected = prev.includes(strId)
        ? prev.filter((f) => f !== strId)
        : [...prev, strId];

      // 🔑 Save selection globally for dashboard
      localStorage.setItem("selected_facilities", JSON.stringify(nextSelected));
      window.dispatchEvent(
        new StorageEvent("storage", { key: "selected_facilities" })
      );

      return nextSelected;
    });
  };

  const toggleSelectAll = () => {
    const next = allSelected ? [] : facilities.map((f) => String(f.id));
    setSelected(next);
    localStorage.setItem("selected_facilities", JSON.stringify(next));
    window.dispatchEvent(
      new StorageEvent("storage", { key: "selected_facilities" })
    );
  };

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      // remove only filtered ones
      setSelected((prev) =>
        prev.filter(
          (id) => !filteredFacilities.some((f) => String(f.id) === id)
        )
      );
    } else {
      // add missing filtered ones
      const ids = new Set(selected);
      filteredFacilities.forEach((f) => ids.add(String(f.id)));
      setSelected([...ids]);
    }
  };

  // Sidebar content
  const sidebarContent = (
    <div className="h-full w-72 bg-gradient-to-b from-white/80 via-white/70 to-white/60 backdrop-blur-2xl border-r border-white/30 shadow-2xl flex flex-col">
      {/* Brand */}
      <div className="px-4 pt-5">
        <div className="rounded-2xl border border-blue-600 bg-blue-600 shadow-md p-3 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-white text-blue-600 grid place-items-center shadow-inner">
            <CarFront className="h-5 w-5 text-blue-600" />
          </div>
          <div className="min-w-0">
            <h1 className="text-sm font-semibold text-white truncate">
              Landlord Panel
            </h1>
            <p className="text-[11px] text-blue-100 truncate">
              Facilities & Revenue
            </p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200/60 mt-4 bg-white/50 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <Building2 className="h-6 w-6 text-blue-600" />
          <div>
            <h2 className="font-semibold text-gray-900 text-sm">
              My Facilities
            </h2>
            <p className="text-xs text-gray-600">({facilityCount} total)</p>
          </div>
        </div>

        <button
          className="lg:hidden text-gray-500 hover:text-gray-700"
          onClick={() => setMobileOpen(false)}
          aria-label="Close facilities panel"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Search + Select-all row */}
      <div className="p-3 border-b border-gray-200/60">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 bg-white/70 backdrop-blur rounded-lg px-2 py-1.5 shadow-sm border flex-1">
            <Search className="h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search facilities"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full bg-transparent text-sm outline-none placeholder-gray-400"
            />
            <button
              className="text-gray-400 hover:text-gray-600"
              aria-label="Filters"
            >
              <Filter className="h-4 w-4" />
            </button>
          </div>

          {/* Select all (global) */}
          <button
            type="button"
            onClick={toggleSelectAll}
            className={[
              "inline-flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border transition",
              "focus:outline-none focus:ring-2 focus:ring-blue-300",
              allSelected
                ? [
                    // Active: soft-mid blue gradient
                    "bg-gradient-to-r from-blue-500 via-blue-600 to-blue-500",
                    "text-white border-transparent hover:from-blue-600 hover:via-blue-700 hover:to-blue-600",
                    "shadow",
                  ].join(" ")
                : [
                    // Inactive: ultra-light blue tint
                    "bg-gradient-to-r from-white via-blue-50 to-white",
                    "text-blue-800 border-blue-200 hover:from-blue-50 hover:via-blue-100 hover:to-blue-50",
                  ].join(" "),
            ].join(" ")}
            title={allSelected ? "Clear all" : "Select all"}
          >
            {allSelected ? (
              <CheckSquare className="h-3.5 w-3.5" />
            ) : (
              <Square className="h-3.5 w-3.5" />
            )}
            {allSelected ? "All selected" : "Select all"}
          </button>
        </div>

        {/* Chips (starts with All) */}
        <div className="flex gap-2 overflow-x-auto mt-3 hide-scrollbar pb-1">
          <motion.button
            onClick={toggleSelectAll}
            whileTap={{ scale: 0.95 }}
            className={[
              "flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all border shadow-sm",
              allSelected
                ? [
                    // Active: soft blue gradient
                    "bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600",
                    "text-white border-blue-300 hover:from-blue-500 hover:via-blue-600 hover:to-blue-500",
                    "shadow",
                  ].join(" ")
                : [
                    // Inactive: light blue tint
                    "bg-gradient-to-r from-white via-blue-50 to-white",
                    "text-blue-800 border-blue-200 hover:from-blue-50 hover:via-blue-100 hover:to-blue-50",
                  ].join(" "),
            ].join(" ")}
          >
            All ({facilities.length})
          </motion.button>

          {/* Also allow “select all in search” */}
          {filteredFacilities.length !== facilities.length && (
            <motion.button
              onClick={toggleSelectAllFiltered}
              whileTap={{ scale: 0.95 }}
              className={[
                "flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all border shadow-sm",
                allFilteredSelected
                  ? [
                      "bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600",
                      "text-white border-blue-300 hover:from-blue-600 hover:via-blue-600 hover:to-blue-600",
                      "shadow",
                    ].join(" ")
                  : [
                      "bg-gradient-to-r from-white via-blue-50 to-white",
                      "text-blue-800 border-blue-200 hover:from-blue-50 hover:via-blue-100 hover:to-blue-50",
                    ].join(" "),
              ].join(" ")}
            >
              This search ({filteredFacilities.length})
            </motion.button>
          )}

          {facilities.map((f) => {
            const isActive = selected.includes(String(f.id));
            return (
              <motion.button
                key={f.id}
                onClick={() => toggleSelect(f.id)}
                whileTap={{ scale: 0.95 }}
                className={[
                  "flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all border shadow-sm",
                  isActive
                    ? [
                        "bg-gradient-to-r from-blue-600 via-blue-600 to-blue-600",
                        "text-white border-blue-300 hover:from-blue-600 hover:via-blue-600 hover:to-blue-600",
                        "shadow",
                      ].join(" ")
                    : [
                        "bg-gradient-to-r from-white via-blue-50 to-white",
                        "text-blue-800 border-blue-200 hover:from-blue-50 hover:via-blue-100 hover:to-blue-50",
                      ].join(" "),
                ].join(" ")}
                title={f.name}
              >
                {f.name}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Facility List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {filteredFacilities.length === 0 ? (
          <div className="px-4 py-6 text-sm text-gray-400 text-center">
            No facilities found
          </div>
        ) : (
          filteredFacilities.map((f) => {
            const isSelected = selected.includes(String(f.id));
            return (
              <motion.div
                key={f.id}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                className={[
                  "relative rounded-2xl border cursor-pointer p-4 transition-all backdrop-blur-lg",
                  isSelected
                    ? "bg-blue-50 border-blue-200 shadow-md"
                    : "bg-white border-gray-200 hover:shadow-lg hover:border-blue-200",
                ].join(" ")}
                onClick={() => toggleSelect(f.id)}
              >
                {/* Active Glow Bar */}
                <span
                  className={[
                    "absolute left-0 top-0 h-full w-1 rounded-l-2xl transition-all",
                    isSelected ? "bg-blue-600" : "bg-blue-100",
                  ].join(" ")}
                />

                <div className="flex items-start gap-3">
                  {/* Checkbox look */}
                  <span
                    className={[
                      "mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-lg border transition-all",
                      isSelected
                        ? "border-blue-600 bg-blue-50 text-blue-700 ring-2 ring-blue-200/60"
                        : "border-gray-300 bg-white text-gray-400 group-hover:border-blue-200",
                    ].join(" ")}
                  >
                    {isSelected ? (
                      <CheckSquare className="h-4 w-4" />
                    ) : (
                      <span className="h-2.5 w-2.5 rounded-full bg-gray-300 group-hover:bg-blue-300 transition" />
                    )}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <h3
                        className={[
                          "truncate text-sm font-semibold transition-colors",
                          isSelected ? "text-blue-900" : "text-gray-900",
                        ].join(" ")}
                        title={f.name}
                      >
                        {f.name}
                      </h3>
                      <ChevronRight className="h-4 w-4 text-blue-200 group-hover:text-blue-300" />
                    </div>

                    <p className="mt-1 text-[11px] font-medium text-gray-600">
                      Facility ID: {f.id}
                    </p>

                    {/* Status */}
                    <div className="mt-3 flex items-center gap-2">
                      <span className="h-3.5 w-3.5 rounded-full bg-green-500 shadow-md" />
                      <p className="text-xs font-medium text-green-700">
                        Active
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-gray-200/70 text-xs text-gray-500 bg-white/50 backdrop-blur">
        © {new Date().getFullYear()} Landlord Portal
      </div>
    </div>
  );

  return (
    <div>
      {/* Desktop Sidebar */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            transition={{ type: "spring", stiffness: 100, damping: 20 }}
            className="hidden lg:block fixed left-0 top-0 bottom-0 z-40"
          >
            {sidebarContent}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop Toggle */}
      <button
        onClick={() => setOpen(!open)}
        className={`hidden lg:flex items-center justify-center fixed top-4 transition-all duration-300 ${
          open ? "left-72" : "left-4"
        } bg-white shadow-md border rounded-full p-2 z-50`}
        aria-label={open ? "Close sidebar" : "Open sidebar"}
      >
        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileOpen && (
          <div className="fixed inset-0 z-50">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: "spring", stiffness: 100, damping: 20 }}
              className="absolute left-0 top-0 bottom-0"
            >
              {sidebarContent}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
